import {
  Box,
  Stack,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Tooltip,
  Typography,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Button } from '@liminal/web-ui/primitives/Button';
import { Checkbox } from '@liminal/web-ui/forms/Checkbox';
import { Select } from '@liminal/web-ui/forms/Select';
import { Pagination } from '@liminal/web-ui/primitives/Pagination';
import { Snackbar } from '@liminal/web-ui/feedback/Snackbar';
import { styled } from '@mui/material/styles';
import { formatDate, formatOrderDate } from '@liminal/utils/date';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { SortableTableCell } from '@liminal/web-ui/display/SortableTableCell';
import { SearchInput } from '@liminal/web-ui/forms/SearchInput';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { personaStatusVariant, personaStatusLabel, KYC_STATUS_NOT_STARTED } from '@/components/ui/statusHelpers';
import { UserIdLink } from '@/components/ui/UserIdLink';
import { useUsersPage, USERS_PAGE_SIZE } from '@/features/users/hooks/useUsersPage';


// Persona-only filter values. The list-page batch enrichment only consults
// kyc.verified_identities (Persona inquiry status), not ramp provider statuses,
// so the filter is honest about being Persona-scoped. The KYC column shows the
// Persona inquiry state itself, so an expired inquiry reads Expired rather than
// the Pending it filters as; the per-provider breakdown is on the detail page
// (ProfileKycSection).
const PERSONA_STATUS_OPTIONS = [
  { value: '', label: 'All Persona Statuses' },
  { value: 'KYC_STATUS_VERIFIED', label: 'Approved' },
  { value: 'KYC_STATUS_PENDING', label: 'Pending' },
  { value: 'KYC_STATUS_UNDER_REVIEW', label: 'Under Review' },
  { value: 'KYC_STATUS_NEEDS_INFO', label: 'Needs Info' },
  { value: 'KYC_STATUS_REJECTED', label: 'Rejected' },
  { value: 'KYC_STATUS_SUSPENDED', label: 'Suspended' },
  { value: KYC_STATUS_NOT_STARTED, label: 'Not Started' },
];

const PLATFORM_OPTIONS = [
  { value: '', label: 'All Platforms' },
  { value: 'PLATFORM_IOS', label: 'iOS' },
  { value: 'PLATFORM_ANDROID', label: 'Android' },
  { value: 'PLATFORM_WEB', label: 'Web' },
];

const PLATFORM_LABELS: Record<string, string> = {
  PLATFORM_IOS: 'iOS',
  PLATFORM_ANDROID: 'Android',
  PLATFORM_WEB: 'Web',
};

const AvatarColorDot = styled(Box)(({ theme }) => ({
  width: 20,
  height: 20,
  borderRadius: '50%',
  border: `1px solid ${theme.palette.divider}`,
}));

const ActionBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.action.hover,
  borderRadius: theme.shape.borderRadius,
}));

// The list shows only what the search response already carries. It used to
// fetch a suspension and a per-provider KYC breakdown for every row: 50 extra
// requests per page, 100 with CORS preflights, which tripped the admin API's
// Cloud Armor rate ban (120 requests/min per IP) after one page load and a few
// keystrokes. Both are one click away on the detail page.
export function UsersPage() {
  const {
    navigate,
    query, kycStatusFilter, platformFilter,
    setQuery, setKycStatusFilter, setPlatformFilter,
    offset, setOffset, totalCount,
    profiles, sort, isLoading, isError, columnCount,
    selectedIds, allPageSelected, somePageSelected,
    handleToggleAll, handleToggleOne, clearSelection,
    isExporting, handleExportSelectedCsv, handleExportAllFilteredCsv,
    snackbar,
  } = useUsersPage();

  return (
    <Stack spacing={3}>
      {/* Filters */}
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="Search users..."
          width={300}
        />
        <Select
          variant="filter"
          aria-label="Persona status"
          size="sm"
          width={180}
          value={kycStatusFilter}
          onChange={setKycStatusFilter}
          options={PERSONA_STATUS_OPTIONS}
        />
        <Select
          variant="filter"
          aria-label="Platform"
          size="sm"
          width={160}
          value={platformFilter}
          onChange={setPlatformFilter}
          options={PLATFORM_OPTIONS}
        />
      </Stack>

      {/* Action bar (shown when users are selected) */}
      {selectedIds.size > 0 && (
        <ActionBar>
          <Typography variant="body2" fontWeight={600}>
            {selectedIds.size} selected
          </Typography>
          <Button size="sm" variant="secondary" onClick={handleExportSelectedCsv}>
            Export Selected (CSV)
          </Button>
          {totalCount > 0 && (
            <Button
              size="sm"
              variant="primary"
              onClick={handleExportAllFilteredCsv}
              loading={isExporting}
            >
              {`Export All ${totalCount} Filtered (CSV)`}
            </Button>
          )}
          <Button size="sm" variant="link" onClick={clearSelection}>
            Clear
          </Button>
        </ActionBar>
      )}

      {/* Data Table. The table is wider than the viewport on purpose;
          horizontal scroll within the container keeps the row dense without
          clipping. */}
      <Card variant="elevated" padding="none">
        <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    aria-label="Select every user on this page"
                    checked={allPageSelected}
                    indeterminate={somePageSelected && !allPageSelected}
                    onChange={handleToggleAll}
                    disabled={isLoading || profiles.length === 0}
                  />
                </TableCell>
                <SortableTableCell column="userId" sort={sort}>
                  User ID
                </SortableTableCell>
                <SortableTableCell column="email" sort={sort}>
                  Email
                </SortableTableCell>
                <SortableTableCell column="firstName" sort={sort}>
                  First Name
                </SortableTableCell>
                <SortableTableCell column="lastName" sort={sort}>
                  Last Name
                </SortableTableCell>
                {/* Unsorted: KYC is enrichment on the search response, not a
                    profile field, so the server has nothing to order by. */}
                <TableCell>KYC</TableCell>
                <SortableTableCell column="platforms" sort={sort}>
                  Platforms
                </SortableTableCell>
                <TableCell>Avatar Color</TableCell>
                <SortableTableCell column="createdAt" sort={sort}>
                  Created At
                </SortableTableCell>
                <SortableTableCell column="isOnboardingCompleted" sort={sort}>
                  Onboarding
                </SortableTableCell>
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={columnCount}
              isLoading={isLoading}
              isError={isError}
              isEmpty={profiles.length === 0}
              emptyMessage="No users found. Try adjusting your search or filters."
              errorMessage="Could not load users."
            >
              {profiles.map((ep) => {
                      const user = ep.profile;
                      if (!user) return null;
                      const userId = user.userId ?? '';
                      return (
                        <TableRow key={userId} hover>
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              aria-label={`Select ${user.email ?? userId}`}
                              checked={selectedIds.has(userId)}
                              onChange={() => handleToggleOne(userId)}
                            />
                          </TableCell>
                          <TableCell>
                            <UserIdLink userId={userId} />
                          </TableCell>
                          <TableCell
                            sx={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/users/${userId}`)}
                          >
                            <Typography fontWeight={600}>{user.email ?? '\u2014'}</Typography>
                          </TableCell>
                          <TableCell>{user.firstName ?? '\u2014'}</TableCell>
                          <TableCell>{user.lastName ?? '\u2014'}</TableCell>
                          <TableCell>
                            <StatusPill
                              label={personaStatusLabel(ep.personaState)}
                              tone={personaStatusVariant(ep.personaState)}
                              size="sm"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {ep.platforms && ep.platforms.length > 0
                                ? ep.platforms.map((p) => (
                                    <StatusPill
                                      key={p}
                                      label={PLATFORM_LABELS[p] ?? p}
                                      tone="neutral"
                                      size="sm"
                                    />
                                  ))
                                : '\u2014'}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {user.avatarColor ? (
                              <AvatarColorDot sx={{ backgroundColor: user.avatarColor }} />
                            ) : (
                              '\u2014'
                            )}
                          </TableCell>
                          <TableCell>
                            {user.createdAt ? (
                              <Tooltip title={formatOrderDate(user.createdAt)} placement="top" arrow>
                                <Box component="span">{formatDate(user.createdAt, { month: 'short' })}</Box>
                              </Tooltip>
                            ) : (
                              '\u2014'
                            )}
                          </TableCell>
                          <TableCell>
                            <StatusPill
                              label={user.isOnboardingCompleted ? 'Yes' : 'No'}
                              tone={user.isOnboardingCompleted ? 'success' : 'neutral'}
                              size="sm"
                            />
                          </TableCell>
                        </TableRow>
                      );
              })}
            </DataTableBody>
          </Table>
        </TableContainer>
      </Card>

      <Pagination
        offset={offset}
        pageSize={USERS_PAGE_SIZE}
        count={profiles.length}
        total={totalCount}
        onOffsetChange={setOffset}
        disabled={isLoading}
      />

      <Snackbar {...snackbar.snackbarProps} />
    </Stack>
  );
}
